import { useEffect, useMemo, useRef, useState } from "react";
import { Header } from "@/components/landing/Header";
import { HeroSection } from "@/components/landing/HeroSection";
import { LandingChatWidget } from "@/components/landing/LandingChatWidget";
import { SocialProofBar } from "@/components/landing/SocialProofBar";
import { FeaturesSection } from "@/components/landing/FeaturesSection";
import { NicheSection } from "@/components/landing/NicheSection";
import { PricingSection } from "@/components/landing/PricingSection";
import { FAQSection } from "@/components/landing/FAQSection";
import { AffiliateSection } from "@/components/landing/AffiliateSection";
import { TestimonialsSection } from "@/components/landing/TestimonialsSection";
import { buildFaqSchema } from "@/components/landing/faqData";
import { Footer } from "@/components/landing/Footer";
import { usePageTracking, useMultipleSectionTracking } from "@/hooks/usePageTracking";
import { SeoHead } from "@/components/seo/SeoHead";
import { trackEvent } from "@/lib/analytics";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";

type LandingConfig = {
  title: string;
  description: string;
  keywords: string;
  defaultSectionId?: string;
  /** When set, overrides DEFAULT_ALTERNATES in SeoHead. Use for EN-only pages. */
  alternates?: Array<{ hreflang: string; path: string }>;
  heroBadge: string;
  heroTitlePrefix: string;
  heroTitleHighlight: string;
  heroTitleSuffix: string;
  heroDescription: string;
  heroDescriptionStrong: string;
  heroPrimaryCta: string;
  ctaTitlePrefix: string;
  ctaTitleHighlight: string;
  ctaDescription: string;
  ctaDescriptionStrong: string;
  ctaButtonLabel: string;
  pricingTitlePrefix: string;
  pricingTitleHighlight: string;
  pricingDescription: string;
};

const LANDING_CONFIGS: Record<string, LandingConfig> = {
  "/": {
    title: "Qlivar — AI Chatbot, Digital Products & Analytics for Your Profile",
    description:
      "Qlivar turns your social media profile into a conversion page with digital products, analytics, and AI automation. Start free with your bio page.",
    keywords:
      "AI chatbot for profile page, AI chatbot builder no code, embed AI chatbot no code, chatbot for small business, sell digital products without website, sell ebook from instagram, track instagram link clicks, free link click tracker, social media link analytics, chatbot para site, vender produtos digitais instagram, rastrear cliques no instagram, creator monetization tool, automate customer replies 24/7, profile page with AI chatbot",
    heroBadge: "Profile page, analytics, digital sales, and AI automation in one place",
    heroTitlePrefix: "Your AI chatbot:",
    heroTitleHighlight: "always online,",
    heroTitleSuffix: "always selling.",
    heroDescription: "Add an AI chatbot to your profile page, sell digital products, and see what drives revenue —",
    heroDescriptionStrong: "no website, no code, no credit card.",
    heroPrimaryCta: "Create my page",
    ctaTitlePrefix: "Sua audiência já existe.",
    ctaTitleHighlight: "Hora de monetizar.",
    ctaDescription: "Responda leads, venda produtos e saiba o que converte —",
    ctaDescriptionStrong: "tudo num único link, grátis para começar.",
    ctaButtonLabel: "Criar minha página grátis",
    pricingTitlePrefix: "Simple pricing.",
    pricingTitleHighlight: "Real value.",
    pricingDescription: "Start free with your bio page and basic analytics. Upgrade to Business for AI chatbot and advanced AI tools.",
  },
  "/pricing": {
    title: "Qlivar Pricing — Profile Page, Store & AI Agent | Starter $6, Pro $12",
    description:
      "Compare Qlivar plans: Free bio page, Starter $6/mo for lead capture, Pro $12/mo for store and custom domain, Business for AI chatbot.",
    keywords:
      "qlivar pricing, AI agent pricing, creator tools pricing comparison, digital product store pricing, qlivar plans 2026, free profile page builder pricing",
    defaultSectionId: "pricing",
    heroBadge: "Transparent pricing for every stage",
    heroTitlePrefix: "Find the",
    heroTitleHighlight: "right plan",
    heroTitleSuffix: "for your growth",
    heroDescription: "Compare Free, Starter, Pro, and Business with clear features and no hidden fees —",
    heroDescriptionStrong: "pay only when you're ready.",
    heroPrimaryCta: "Compare plans now",
    ctaTitlePrefix: "Choose a plan and",
    ctaTitleHighlight: "start today.",
    ctaDescription: "Go from free to advanced monetization at your own pace —",
    ctaDescriptionStrong: "Starter $6, Pro $12, Business $60.",
    ctaButtonLabel: "Start free now",
    pricingTitlePrefix: "Compare plans.",
    pricingTitleHighlight: "Scale faster.",
    pricingDescription: "From first links to advanced growth tools, choose the plan that matches your stage.",
  },
  "/analytics": {
    title: "Qlivar Analytics — Track Which Social Links Drive Real Sales",
    description:
      "Track where Instagram, TikTok, and Google visitors come from. See click counts, device breakdown, and which links convert. Free for creators.",
    keywords:
      "track instagram link clicks, instagram link click tracker, which links convert to sales, social media link analytics, free link tracker, how many people click my instagram link, link click to sale tracking, rastrear cliques link instagram, analytics de links gratuito, ver de onde vêm os cliques",
    defaultSectionId: "features",
    heroBadge: "Know which posts actually drive revenue",
    heroTitlePrefix: "Track which links",
    heroTitleHighlight: "drive sales,",
    heroTitleSuffix: "not just clicks.",
    heroDescription: "Real analytics per link — click volume, traffic sources, device breakdown, and conversion data —",
    heroDescriptionStrong: "stop guessing, start optimizing.",
    heroPrimaryCta: "Start tracking for free",
    ctaTitlePrefix: "Turn data into",
    ctaTitleHighlight: "growth.",
    ctaDescription: "Stop guessing which posts drive results. Track every click to its conversion source —",
    ctaDescriptionStrong: "from your first view to your next sale.",
    ctaButtonLabel: "Create free account",
    pricingTitlePrefix: "Analytics-ready plans.",
    pricingTitleHighlight: "No complexity.",
    pricingDescription: "Start free with basic click tracking. Upgrade for per-link conversion data and deeper insights.",
  },
  "/mini-store": {
    title: "Qlivar Mini-Store — Sell Digital Products Without a Website or Shopify",
    description:
      "Sell ebooks, courses, and digital downloads from your Instagram profile. Built-in checkout, instant delivery, zero e-commerce setup. Free to start.",
    keywords:
      "sell digital products without website, sell ebook from instagram, how to sell on instagram without website, digital product store free, sell courses without shopify, sell downloads from social media, sell ebook online free, vender produto digital sem site, loja digital grátis, vender curso pelo instagram, vender ebook sem site",
    defaultSectionId: "features",
    heroBadge: "Sell digital products from your profile — no website needed",
    heroTitlePrefix: "Sell digital products",
    heroTitleHighlight: "without a website",
    heroTitleSuffix: "or Shopify.",
    heroDescription: "Launch your digital store in minutes — ebooks, courses, presets, digital files —",
    heroDescriptionStrong: "built-in checkout, instant delivery, no tech setup.",
    heroPrimaryCta: "Start selling for free",
    ctaTitlePrefix: "Your profile is your",
    ctaTitleHighlight: "storefront.",
    ctaDescription: "Digital products, links, and AI chatbot in one page —",
    ctaDescriptionStrong: "built for creators who sell without a full website.",
    ctaButtonLabel: "Start free and sell",
    pricingTitlePrefix: "Plans that monetize.",
    pricingTitleHighlight: "Built to sell.",
    pricingDescription: "Unlock the digital products store on Starter plan at $6/mo — cancel anytime.",
  },
  "/custom-domain": {
    title: "Qlivar Custom Domain — Brand Your Profile Page with Your Own URL",
    description:
      "Connect your own domain to your Qlivar profile page. Build trust with clients and sponsors. Digital store on Pro, AI chatbot on Business.",
    keywords:
      "custom domain profile page, branded landing page creator, custom url social media, professional creator page, custom domain page builder, personal brand link page, branded profile page creator",
    defaultSectionId: "features",
    heroBadge: "Build trust with your own branded URL",
    heroTitlePrefix: "Use your own",
    heroTitleHighlight: "custom domain",
    heroTitleSuffix: "on your profile page.",
    heroDescription: "Turn your Qlivar page into links.yourbrand.com — more professional to clients and sponsors —",
    heroDescriptionStrong: "AI chatbot, store, and analytics all under your brand.",
    heroPrimaryCta: "Start with your brand",
    ctaTitlePrefix: "Your brand deserves",
    ctaTitleHighlight: "your domain.",
    ctaDescription: "Improve credibility with a branded URL, advanced analytics, and optional AI automation —",
    ctaDescriptionStrong: "from one professional profile page.",
    ctaButtonLabel: "Create branded page",
    pricingTitlePrefix: "Brand-first pricing.",
    pricingTitleHighlight: "Upgrade when ready.",
    pricingDescription: "Custom domain unlocks on Pro plan at $12/mo. Upgrade to Business for AI chatbot and AI lead qualification.",
  },
  "/for-creators": {
    title: "Qlivar for Creators — AI Chatbot, Store & Analytics",
    description:
      "The creator toolkit: profile page, monetization tools, and analytics free. Add AI chatbot on Business when you need it. No website required.",
    keywords:
      "creator monetization tools free, chatbot for content creators, sell digital products as creator, instagram creator tools free, how to make money from social media, automate instagram replies, creator tools no code, chatbot para criadores de conteúdo, monetização criadores grátis, ferramentas para criadores",
    alternates: [
      { hreflang: "x-default", path: "/for-creators" },
      { hreflang: "en-US", path: "/for-creators" },
    ],
    defaultSectionId: "features",
    heroBadge: "Made for creators who want to earn, not just post",
    heroTitlePrefix: "Get paid from social media",
    heroTitleHighlight: "with AI",
    heroTitleSuffix: "doing the work.",
    heroDescription: "Mini-store sells digital products 24/7, analytics shows what converts, and AI automation is available when you need it —",
    heroDescriptionStrong: "start free with your creator page, no website required.",
    heroPrimaryCta: "Start creator page free",
    ctaTitlePrefix: "Creators earn more with",
    ctaTitleHighlight: "smarter tools.",
    ctaDescription: "Digital sales and conversion data in one profile page, with AI replies available on Business —",
    ctaDescriptionStrong: "without platform lock-in.",
    ctaButtonLabel: "Create free creator page",
    pricingTitlePrefix: "Creator-friendly plans.",
    pricingTitleHighlight: "Made to scale.",
    pricingDescription: "Start free with your creator page. Add digital store on Pro and AI chatbot on Business as your business grows.",
  },
  "/linktree-alternative": {
    title: "The Linktree Alternative That Has AI Chatbot + Digital Store | Qlivar",
    description:
      "Unlike Linktree, Qlivar adds a digital products store, A/B testing, click-to-sale analytics, and AI chatbot on Business. Built to convert.",
    keywords:
      "linktree alternative with AI chatbot, best linktree alternative 2026, linktree alternative free with store, profile page with AI assistant, linktree alternative digital products, linktree vs qlivar AI, free linktree alternative 2026",
    alternates: [
      { hreflang: "x-default", path: "/linktree-alternative" },
      { hreflang: "en-US", path: "/linktree-alternative" },
    ],
    defaultSectionId: "pricing",
    heroBadge: "The profile page that converts and sells — features Linktree doesn't have",
    heroTitlePrefix: "More than links —",
    heroTitleHighlight: "AI chatbot,",
    heroTitleSuffix: "store and analytics.",
    heroDescription: "Qlivar adds AI that answers leads, digital products checkout, and revenue analytics to your profile —",
    heroDescriptionStrong: "features Linktree doesn't have on any plan.",
    heroPrimaryCta: "Try Qlivar free",
    ctaTitlePrefix: "Ready to go beyond",
    ctaTitleHighlight: "basic link pages?",
    ctaDescription: "Digital store, click analytics, and optional AI chatbot —",
    ctaDescriptionStrong: "with clear pricing and no lock-in.",
    ctaButtonLabel: "Switch and start free",
    pricingTitlePrefix: "Compare value.",
    pricingTitleHighlight: "Choose smarter.",
    pricingDescription: "Free plan includes unlimited links. Upgrade to Business for AI chatbot and AI lead qualification.",
  },
};

const Index = () => {
  const { language, locale } = useLanguage();
  const location = useLocation();
  const landingConfig = useMemo(
    () => LANDING_CONFIGS[location.pathname] || LANDING_CONFIGS["/"],
    [location.pathname]
  );
  const pageTrackingName = location.pathname === "/" ? "landing_page" : `landing${location.pathname.replace(/\//g, "_")}`;
  const isEnglish = language === "en";
  const shouldUseEnglishHeroOverride = isEnglish && location.pathname !== "/";
  const heroTitleFromConfig = [
    landingConfig.heroTitlePrefix,
    landingConfig.heroTitleHighlight,
    landingConfig.heroTitleSuffix,
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  const heroSubtitleFromConfig = [
    landingConfig.heroDescription,
    landingConfig.heroDescriptionStrong,
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  // Track page-level metrics
  usePageTracking({ pageName: pageTrackingName });
  
  // Track section visibility
  const { createSectionObserver } = useMultipleSectionTracking(pageTrackingName);

  const heroRef = useRef<HTMLDivElement>(null);
  const storyRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);
  const faqRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (heroRef.current) createSectionObserver('hero', 1)(heroRef.current);
    if (storyRef.current) createSectionObserver('story', 2)(storyRef.current);
    if (featuresRef.current) createSectionObserver('features', 3)(featuresRef.current);
    if (pricingRef.current) createSectionObserver('pricing', 4)(pricingRef.current);
    if (faqRef.current) createSectionObserver('faq', 5)(faqRef.current);
  }, [createSectionObserver]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const source = params.get("utm_source");
    const medium = params.get("utm_medium");
    const campaign = params.get("utm_campaign");

    if (source || medium || campaign) {
      trackEvent(
        "ad_landing_view",
        "acquisition",
        `${location.pathname}|${source || "direct"}|${medium || "none"}|${campaign || "none"}`
      );
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const targetFromHash = location.hash?.replace("#", "");
    const targetSection = targetFromHash || landingConfig.defaultSectionId;

    if (!targetSection) return;

    let attempts = 0;
    const maxAttempts = 20;
    let settleTimer: number | undefined;

    const getHeaderOffset = () => {
      const header = document.getElementById("site-header");
      const headerHeight = header?.getBoundingClientRect().height ?? 64;
      return Math.ceil(headerHeight) + 12;
    };

    const performScroll = (behavior: ScrollBehavior) => {
      const section = document.getElementById(targetSection);
      if (!section) return false;

      const headerOffset = getHeaderOffset();
      const targetTop = section.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: Math.max(0, targetTop), behavior });
      return true;
    };

    const scrollToTarget = () => {
      if (performScroll("smooth")) {
        settleTimer = window.setTimeout(() => {
          performScroll("auto");
        }, 280);
        return;
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        window.setTimeout(scrollToTarget, 50);
      }
    };

    const timer = window.setTimeout(scrollToTarget, 0);

    return () => {
      window.clearTimeout(timer);
      if (settleTimer) {
        window.clearTimeout(settleTimer);
      }
    };
  }, [landingConfig.defaultSectionId, location.hash]);

  const [chatOpen, setChatOpen] = useState(false);
  useEffect(() => {
    const handleOpenChat = () => setChatOpen(true);
    window.addEventListener('open-landing-chat', handleOpenChat);
    return () => {
      window.removeEventListener('open-landing-chat', handleOpenChat);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#fffaf5]">
      <SeoHead
        title={landingConfig.title}
        description={landingConfig.description}
        path={location.pathname}
        lang={language}
        locale={locale}
        keywords={landingConfig.keywords}
        alternates={landingConfig.alternates}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Qlivar",
            url: "https://qlivar.com",
            description:
              "Creator monetization platform — turn your link in bio into a storefront, capture leads, and let an AI agent close sales while you sleep.",
            potentialAction: {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate: "https://qlivar.com/blog?q={search_term_string}",
              },
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Qlivar",
            url: "https://qlivar.com",
            logo: "https://qlivar.com/uploads/81900d3c-9f15-497f-a3d9-c0d5a7ab9ba0.svg",
            sameAs: [
              "https://www.instagram.com/qlivar",
              "https://x.com/qlivar",
              "https://www.tiktok.com/@qlivar",
              "https://www.linkedin.com/company/qlivar",
            ],
            areaServed: [
              { "@type": "Country", name: "United States" },
              { "@type": "Country", name: "Brazil" },
              { "@type": "Place", name: "Europe" },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Qlivar",
            applicationCategory: "BusinessApplication",
            applicationSubCategory: "Creator Economy · Link in Bio · Monetization",
            operatingSystem: "Web",
            url: "https://qlivar.com",
            inLanguage: ["en", "pt-BR"],
            areaServed: [
              { "@type": "Country", name: "United States" },
              { "@type": "Country", name: "Brazil" },
              { "@type": "Place", name: "Europe" },
            ],
            description:
              "Qlivar is the creator economy platform that turns your link in bio into a real storefront — digital products, click analytics, A/B testing, link scheduling, and your own custom domain. AI chatbot and AI lead qualification are available on the Business plan, so creators can sell while they create.",
            featureList: [
              "AI chatbot on Business — answers visitors and leads 24/7 automatically",
              "Digital products mini-store — sell ebooks and courses without a website",
              "Click analytics — track which social media links drive revenue",
              "A/B testing for profile links",
              "Link scheduling — publish and hide links on a schedule",
              "Custom domain — use your own branded URL",
              "Affiliate program — earn commissions promoting other creators",
              "No coding required — setup in under 5 minutes",
            ],
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
              description: "Free plan includes bio page, unlimited links, and basic analytics — no credit card required",
            },
          },
          buildFaqSchema(language),
        ]}
      />
      <Header />
      <main className="overflow-hidden">
        <div ref={heroRef}>
          <HeroSection
            badgeText={shouldUseEnglishHeroOverride ? landingConfig.heroBadge : undefined}
            title={shouldUseEnglishHeroOverride ? heroTitleFromConfig : undefined}
            subtitle={shouldUseEnglishHeroOverride ? heroSubtitleFromConfig : undefined}
            primaryCtaLabel={shouldUseEnglishHeroOverride ? landingConfig.heroPrimaryCta : undefined}
          />
        </div>
        <div ref={storyRef}>
          <SocialProofBar />
        </div>
        <div ref={featuresRef}>
          <FeaturesSection />
        </div>
        <NicheSection />
        <TestimonialsSection />
        <div ref={pricingRef}>
          <PricingSection
            titlePrefix={landingConfig.pricingTitlePrefix}
            titleHighlight={landingConfig.pricingTitleHighlight}
            description={landingConfig.pricingDescription}
          />
        </div>
        <AffiliateSection />
        <div ref={faqRef}>
          <FAQSection />
        </div>
      </main>
      <Footer />
      <LandingChatWidget isOpen={chatOpen} onOpenChange={setChatOpen} initialQuery="" />
    </div>
  );
};

export default Index;
